import React from "react";
import { InformationCircleIcon } from '@heroicons/react/20/solid';

function InfoCard({ name, info }) {
    return (
        <li className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <InformationCircleIcon className="size-5" />
            </div>
            <div className="flex flex-col">
                <p className="text-sm font-medium text-gray-500">{name}</p>
                <p className="text-base font-semibold text-gray-900">{info || "Non renseigné"}</p>
            </div>
        </li>
    );
}

function InfoGrid({ items }) {
    return (
        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
            {items.map((item) => (
                <InfoCard key={item.name} name={item.name} info={item.info} />
            ))}
        </ul>
    );
}

function formatDate(dateStr) {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });
}

export default function InfosBasiqueAffaire({ affaire, type }) {

    // PMA
    const typePmaLabel = { couplehomo: 'Couple homosexuel', couplehetero: 'Couple hétérosexuel', seul: 'Femme seule' };
    const debutPmaLabel = { oui: 'Débuté', non: 'Non débuté' };

    const infoPma = [
        { name: 'Type', info: typePmaLabel[affaire.type] ?? 'Non communiqué' },
        { name: 'Date ouverture', info: formatDate(affaire.date_creation) },
        { name: 'Début de process PMA', info: debutPmaLabel[affaire.debut_pma] ?? 'Non communiqué' },
    ];

    // MARIAGE
    const valeurImmoLabel = {
        moins30: 'Moins de 30 200€',
        '30800-200': 'Entre 30 800€ et 200K€',
        '200-500': 'Entre 200 et 500K€',
        plus500: 'Plus de 500K€',
    };
    const ouiNonLabel = { oui: 'Oui', non: 'Non' };

    const infoMariage = [
        { name: 'Déjà marié', info: affaire.marie === 'oui' ? 'Oui' : affaire.marie === 'non' ? 'Non' : 'Non communiqué' },
        { name: 'Date ouverture', info: formatDate(affaire.date_creation) },
        { name: 'Bien immobilier', info: ouiNonLabel[affaire.bien_immo] ?? 'Non communiqué' },
        ...(affaire.bien_immo === 'oui' ? [{ name: 'Valeur du bien', info: valeurImmoLabel[affaire.valeur_immo] ?? 'Non communiqué' }] : []),
    ];

    // IMMOBILIER
    const infoImmo = [
        { name: 'Montant transaction', info: affaire.prix ? `${affaire.prix} €` : 'Non renseigné' },
        { name: 'Date ouverture', info: formatDate(affaire.date_creation) },
        { name: 'Type de bien', info: affaire.type_bien ?? 'Non renseigné' },
        { name: 'Financement', info: affaire.type_financement ?? 'Non renseigné' },
        { name: 'Type achat', info: affaire.type_achat ?? 'Non renseigné' },
    ];

    // PACS
    const infoPacs = [
        { name: 'Enfant', info: ouiNonLabel[affaire.enfant] ?? 'Non communiqué' },
        { name: 'Date ouverture', info: formatDate(affaire.date_creation) },
        { name: 'Bien immobilier', info: ouiNonLabel[affaire.bien_immo] ?? 'Non communiqué' },
    ];

    // CERTIFICATION
    const infoCertification = [
        { name: 'Date ouverture', info: formatDate(affaire.date_creation) },
    ];

    // DONATION
    const typeDonationLabel = {
        immobilier: 'Immobilier',
        argent: 'Financière',
        voiture: 'Voiture',
        bijoux: 'Bijoux',
        autre: 'Autre',
    };
    const normalizedDonationType = String(affaire?.type_donation ?? '').trim().toLowerCase();

    const infoDonation = [
        { name: 'Type de donation', info: typeDonationLabel[normalizedDonationType] ?? 'Non communiqué' },
        { name: 'Date ouverture', info: formatDate(affaire.date_creation) },
        { name: 'Nb bénéficiaire', info: affaire.nb_beneficiaire },
    ];

    // DIVORCE
    const contratMariageInfo = affaire.contrat_mariage === 'oui'
        ? `Oui${affaire.regime_matrimonial ? ` — ${affaire.regime_matrimonial}` : ''}`
        : affaire.contrat_mariage === 'non' ? 'Pas de contrat' : 'Non renseigné';

    const infoDivorce = [
        { name: 'Date ouverture', info: formatDate(affaire.date_creation) },
        { name: 'Contrat de mariage', info: contratMariageInfo },
        { name: 'Valeur bien immo', info: valeurImmoLabel[affaire.valeur_immo] ?? 'Aucun' },
    ];

    // BAIL
    const typeBienLabel = { appartement: 'Appartement', maison: 'Maison' };

    const infoBail = [
        { name: 'Type de bien', info: typeBienLabel[affaire.type_bien] ?? 'Non renseigné' },
        { name: 'Date ouverture', info: formatDate(affaire.date_creation) },
        { name: 'Nb locataire', info: affaire.nb_locataire },
    ];

    const infoByType = {
        immo: infoImmo,
        pma: infoPma,
        mariage: infoMariage,
        pacs: infoPacs,
        certification: infoCertification,
        donation: infoDonation,
        divorce: infoDivorce,
        bail: infoBail,
    };

    const items = infoByType[type];
    if (!items) return null;

    return <InfoGrid items={items} />;
}
